import { useI18n } from "@/lib/i18n";
import { useLocation, useNavigate } from "react-router-dom";
import { useCallback } from "react";
import { ThemeLogo } from "@/components/ThemeLogo";
import { Phone, MessageCircle, MapPin, Clock } from "lucide-react";
import { useCookieConsent } from "@/lib/cookie-consent";
import { Link } from "react-router-dom";

// Constantes (mesmas do Header para consistência)
const WHATSAPP_NUMBER = "351939507334";
const PHONE_NUMBER = "+351 939 507 334";
const WHATSAPP_MESSAGE = encodeURIComponent("Olá Igor!");

export function Footer() {
  const { t } = useI18n();
  const { openSettings } = useCookieConsent();
  const location = useLocation();
  const navigate = useNavigate();

  const scrollToSection = useCallback(
    (sectionId: string) => {
      const scroll = () => {
        const el = document.getElementById(sectionId);
        if (el) {
          el.scrollIntoView({ behavior: "smooth" });
        }
      };

      if (location.pathname !== "/") {
        navigate("/");
        setTimeout(scroll, 150);
      } else {
        scroll();
      }
    },
    [location.pathname, navigate],
  );

  return (
    <footer className="bg-card border-t border-border/50 pt-16 pb-8">
      <div className="container mx-auto px-4">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-12 mb-12">
          {/* Coluna 1: Branding */}
          <div className="space-y-4">
            <ThemeLogo className="h-10 w-auto" width={60} height={60} />
            <p className="text-muted-foreground text-sm leading-relaxed max-w-xs">
              {t.about.subtitle}. Especialistas em automóveis de confiança com oficina própria Setauto.
            </p>
          </div>

          {/* Coluna 2: Navegação */}
          <div>
            <h4 className="font-bold text-foreground mb-6 uppercase tracking-wider text-xs">Navegação</h4>
            <ul className="space-y-3">
              {[
                { id: "stock", label: t.nav.stock },
                { id: "servicos", label: t.nav.services },
                { id: "sobre", label: t.nav.about },
                { id: "contacto", label: t.nav.contact },
              ].map((link) => (
                <li key={link.id}>
                  <button
                    onClick={() => scrollToSection(link.id)}
                    className="text-muted-foreground hover:text-primary transition-colors text-sm link-underline inline-block"
                  >
                    {link.label}
                  </button>
                </li>
              ))}
            </ul>
          </div>

          {/* Coluna 3: Legal */}
          <div>
            <h4 className="font-bold text-foreground mb-6 uppercase tracking-wider text-xs">Legal</h4>
            <ul className="space-y-3">
              <li>
                <Link to="/privacidade" className="text-muted-foreground hover:text-primary transition-colors text-sm link-underline inline-block">
                  {t.footer.privacy}
                </Link>
              </li>
              <li>
                <button onClick={openSettings} className="text-muted-foreground hover:text-primary transition-colors text-sm link-underline inline-block">
                  Definições de Cookies
                </button>
              </li>
            </ul>
          </div>

          {/* Coluna 4: Contactos */}
          <div>
            <h4 className="font-bold text-foreground mb-6 uppercase tracking-wider text-xs">Contactos</h4>
            <ul className="space-y-4">
              <li className="flex items-start gap-3 text-sm text-muted-foreground">
                <MapPin className="h-4 w-4 text-primary shrink-0 mt-0.5" />
                <span>
                  {t.contact.location}
                  <br />
                  Alverca do Ribatejo, Portugal
                </span>
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <Phone className="h-4 w-4 text-primary shrink-0" />
                <a href={`tel:${PHONE_NUMBER.replace(/\s/g, "")}`} className="hover:text-primary transition-colors">
                  {PHONE_NUMBER}
                </a>
              </li>
              <li className="flex items-center gap-3 text-sm text-muted-foreground">
                <MessageCircle className="h-4 w-4 text-primary shrink-0" />
                <a
                  href={`https://wa.me/${WHATSAPP_NUMBER}?text=${WHATSAPP_MESSAGE}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hover:text-primary transition-colors"
                >
                  WhatsApp
                </a>
              </li>
            </ul>
          </div>

          {/* Coluna 5: Horário */}
          <div>
            <h4 className="font-bold text-foreground mb-6 uppercase tracking-wider text-xs">{t.contact.hours}</h4>
            <div className="flex items-start gap-3 text-sm text-muted-foreground">
              <Clock className="h-4 w-4 text-primary shrink-0 mt-0.5" />
              <p>{t.contact.hoursText}</p>
            </div>
          </div>
        </div>

        {/* Linha de Baixo: Copyright e Powered by */}
        <div className="pt-8 border-t border-border/30 flex flex-col md:flex-row justify-between items-center gap-4 text-[10px] sm:text-xs text-muted-foreground">
          <p>
            © {new Date().getFullYear()} PassCar. {t.footer.rights}
          </p>
          <a
            href="https://mcdp-consulting.com"
            target="_blank"
            rel="noopener noreferrer"
            className="text-[10px] text-muted-foreground/50 hover:text-muted-foreground transition-colors"
          >
            Powered by MCDP Consulting
          </a>
        </div>
      </div>
    </footer>
  );
}
